import { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import type { SpinnerProps } from './Spinner.types';

// Keyframe animations
const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
`;

const stretch = keyframes`
  0%, 40%, 100% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
`;

// Styled components
const SpinnerContainer = styled.div<{ $centered?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  --spinner-size: 32px;

  /* Size variants */
  &[data-size='xs'] {
    --spinner-size: 16px;
  }

  &[data-size='sm'] {
    --spinner-size: 20px;
  }

  &[data-size='md'] {
    --spinner-size: 32px;
  }

  &[data-size='lg'] {
    --spinner-size: 48px;
  }

  &[data-size='xl'] {
    --spinner-size: 64px;
  }

  /* Centered variant */
  ${({ $centered }) =>
    $centered &&
    `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}

  /* Speed variants - slowed down for calmer feel */
  &[data-speed='slow'] {
    --spinner-duration: 2.5s;
  }

  &[data-speed='normal'] {
    --spinner-duration: 1.5s;
  }

  &[data-speed='fast'] {
    --spinner-duration: 1s;
  }

  /* Color variants - use CSS variables with fallback */
  &[data-color='primary'] {
    --spinner-color: var(--color-highlight, var(--colour-highlight, #F68D32));
  }

  &[data-color='secondary'] {
    --spinner-color: var(--color-secondary, var(--colour-secondary, #6b7280));
  }

  &[data-color='white'] {
    --spinner-color: #ffffff;
  }

  &[data-color='inherit'] {
    --spinner-color: currentColor;
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    --spinner-duration: 3s;
  }
`;

const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

// Circular variant - indeterminate
const Circular = styled.div<{ $isAnimated: boolean }>`
  width: var(--spinner-size);
  height: var(--spinner-size);
  ${({ $isAnimated }) => $isAnimated && css`
    animation: ${rotate} var(--spinner-duration, 1s) linear infinite;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation-timing-function: steps(8, end);
  }
`;

const CircularSvg = styled.svg`
  width: 100%;
  height: 100%;
`;

const CircularPathBackground = styled.circle`
  stroke: var(--spinner-color);
  opacity: 0.2;
`;

const CircularPathIndeterminate = styled.circle`
  stroke: var(--spinner-color);
  stroke-linecap: round;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  animation: ${dash} var(--spinner-duration, 1s) ease-in-out infinite;
`;

// Smooth border-based spinner variant (like ProcessingOverlay)
const SmoothSpinner = styled.div`
  width: var(--spinner-size);
  height: var(--spinner-size);
  border: 3px solid var(--spinner-track-color, rgba(243, 243, 243, 0.3));
  border-top-color: var(--spinner-color);
  border-radius: 50%;
  animation: ${rotate} var(--spinner-duration, 1s) linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-timing-function: steps(8, end);
  }
`;

const CircularPathDeterminate = styled.circle`
  stroke: var(--spinner-color);
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  transition: stroke-dashoffset 0.3s ease-out;
`;

// Progress label for determinate mode
const ProgressLabel = styled.span`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: calc(var(--spinner-size) * 0.25);
  font-weight: 600;
  color: var(--spinner-color);
  font-family: ${props => props.theme.typography?.fontFamily || '"Roboto", sans-serif'};
`;

// Dots variant
const DotsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(var(--spinner-size) * 0.15);
  height: var(--spinner-size);
`;

const Dot = styled.span<{ $delay: number }>`
  width: calc(var(--spinner-size) * 0.25);
  height: calc(var(--spinner-size) * 0.25);
  background-color: var(--spinner-color);
  border-radius: 50%;
  animation: ${bounce} var(--spinner-duration, 1s) ease-in-out infinite;
  animation-delay: calc(var(--spinner-duration, 1s) * ${({ $delay }) => $delay});

  @media (prefers-reduced-motion: reduce) {
    animation-timing-function: steps(4, end);
  }
`;

// Bars variant
const BarsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(var(--spinner-size) * 0.1);
  height: var(--spinner-size);
`;

const Bar = styled.span<{ $delay: number }>`
  width: calc(var(--spinner-size) * 0.15);
  height: 100%;
  background-color: var(--spinner-color);
  border-radius: calc(var(--spinner-size) * 0.075);
  animation: ${stretch} var(--spinner-duration, 1s) ease-in-out infinite;
  animation-delay: calc(var(--spinner-duration, 1s) * ${({ $delay }) => $delay});

  @media (prefers-reduced-motion: reduce) {
    animation-timing-function: steps(4, end);
  }
`;

/**
 * Calculate the stroke-dashoffset for determinate progress
 */
function calculateDashOffset(value: number, circumference: number): number {
  const clampedValue = Math.min(100, Math.max(0, value));
  return circumference - (clampedValue / 100) * circumference;
}

/**
 * Spinner component - Loading indicator with multiple variants
 * Supports determinate mode and withLabel option
 *
 * @example
 * ```tsx
 * // Circular spinner (default)
 * <Spinner />
 * <Spinner size="lg" color="primary" />
 *
 * // Dots variant
 * <Spinner variant="dots" />
 *
 * // Bars variant
 * <Spinner variant="bars" speed="fast" />
 *
 * // Centered in container
 * <Spinner centered />
 *
 * // Determinate progress
 * <Spinner progressMode="determinate" value={75} withLabel />
 *
 * // Custom size and thickness
 * <Spinner size={48} thickness={6} />
 * ```
 */
export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  (
    {
      size = 'md',
      color = 'primary',
      variant = 'circular',
      speed = 'normal',
      label = 'Loading...',
      centered = false,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
      progressMode = 'indeterminate',
      value = 0,
      thickness = 4,
      withLabel = false,
      labelFormatter = (v) => `${Math.round(v)}%`,
      customColor,
      smooth = false,
    },
    ref
  ) => {
    // Calculate size value
    const getSizeValue = (): string | undefined => {
      if (typeof size === 'number') {
        return `${size}px`;
      }
      return undefined;
    };

    const sizeValue = getSizeValue();

    // Build inline styles
    const inlineStyles: CSSProperties = {
      ...(sizeValue &&
        ({
          '--spinner-size': sizeValue,
        } as CSSProperties)),
      ...(customColor && ({
        '--spinner-color': customColor,
      } as CSSProperties)),
    };

    // Calculate SVG properties for determinate mode
    const radius = 20;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = calculateDashOffset(value, circumference);

    // Render variant-specific content
    const renderSpinner = () => {
      switch (variant) {
        case 'dots':
          return (
            <DotsContainer>
              <Dot $delay={0} />
              <Dot $delay={0.15} />
              <Dot $delay={0.3} />
            </DotsContainer>
          );

        case 'bars':
          return (
            <BarsContainer>
              <Bar $delay={0} />
              <Bar $delay={0.15} />
              <Bar $delay={0.3} />
              <Bar $delay={0.45} />
            </BarsContainer>
          );

        case 'circular':
        default:
          if (progressMode === 'indeterminate') {
            // Use smooth border-based spinner for consistent rotation rhythm
            if (smooth) {
              return <SmoothSpinner />;
            }
            // Default stroke-dasharray animation
            return (
              <Circular $isAnimated={true}>
                <CircularSvg viewBox="0 0 50 50">
                  <CircularPathIndeterminate
                    cx="25"
                    cy="25"
                    r={radius}
                    fill="none"
                    strokeWidth={thickness}
                  />
                </CircularSvg>
              </Circular>
            );
          }

          // Determinate or static mode
          return (
            <Circular $isAnimated={false}>
              <CircularSvg viewBox="0 0 50 50">
                {/* Background circle */}
                <CircularPathBackground
                  cx="25"
                  cy="25"
                  r={radius}
                  fill="none"
                  strokeWidth={thickness}
                />
                {/* Progress circle */}
                <CircularPathDeterminate
                  cx="25"
                  cy="25"
                  r={radius}
                  fill="none"
                  strokeWidth={thickness}
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                />
              </CircularSvg>
              {withLabel && (
                <ProgressLabel aria-hidden="true">
                  {labelFormatter(value)}
                </ProgressLabel>
              )}
            </Circular>
          );
      }
    };

    // Build aria-label based on mode
    const ariaLabel =
      progressMode === 'determinate' || progressMode === 'static'
        ? `${label} ${Math.round(value)}%`
        : label;

    return (
      <SpinnerContainer
        ref={ref}
        className={className}
        style={inlineStyles}
        role="progressbar"
        aria-live="polite"
        aria-label={ariaLabel}
        aria-valuenow={progressMode !== 'indeterminate' ? Math.round(value) : undefined}
        aria-valuemin={progressMode !== 'indeterminate' ? 0 : undefined}
        aria-valuemax={progressMode !== 'indeterminate' ? 100 : undefined}
        data-variant={variant}
        data-size={typeof size === 'string' ? size : 'custom'}
        data-color={customColor ? 'custom' : color}
        data-speed={speed}
        data-progress-mode={progressMode}
        data-smooth={smooth ? 'true' : undefined}
        data-centered={centered ? 'true' : undefined}
        data-testid={dataTestId || testId}
        data-id={dataId}
        $centered={centered}
      >
        {renderSpinner()}
        <ScreenReaderOnly>{ariaLabel}</ScreenReaderOnly>
      </SpinnerContainer>
    );
  }
);

Spinner.displayName = 'Spinner';

export default Spinner;
